import { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TreeStructure, Plus, Trash, Pencil, X, Lock,
  Lightning, Shield, Globe, CaretRight, CheckCircle,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

function GovernancePanel({ governance, available, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(governance || {});

  useEffect(() => { setForm(governance || {}); }, [governance]);

  function save() {
    onUpdate(form);
    setEditing(false);
  }

  const signalCategories = ['schema_drift', 'volume_anomaly', 'latency_baseline', 'error_pattern', 'integration_topology', 'query_pattern'];

  return (
    <div className="space-y-3" data-testid="governance-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield size={14} className="text-kova-purple" />
          <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">Governance Settings</span>
        </div>
        {!editing && <button onClick={() => setEditing(true)} className="text-[9px] font-mono text-kova-cyan hover:underline" data-testid="edit-governance-btn">Edit</button>}
      </div>

      {editing ? (
        <div className="space-y-3 p-3 border border-kova-cyan/12 bg-kova-bg">
          {/* Signal Exchange Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] font-medium">Signal Exchange</div>
              <div className="text-[9px] font-mono text-kova-slate">Share anonymized intelligence with other constellations</div>
            </div>
            {available.signal_exchange_available ? (
              <button
                onClick={() => setForm(f => ({ ...f, signal_exchange_enabled: !f.signal_exchange_enabled }))}
                className={`w-10 h-5 rounded-full transition-colors ${form.signal_exchange_enabled ? 'bg-kova-cyan' : 'bg-kova-cyan/12'}`}
                data-testid="signal-exchange-toggle"
              >
                <div className={`w-4 h-4 bg-white rounded-full transition-transform ${form.signal_exchange_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            ) : (
              <span className="text-[9px] font-mono text-kova-slate flex items-center gap-1"><Lock size={10} />Upgrade required</span>
            )}
          </div>

          {/* Signal Categories */}
          {form.signal_exchange_enabled && (
            <div>
              <div className="text-[10px] font-mono text-kova-slate mb-2">Signal categories to share</div>
              <div className="flex flex-wrap gap-1.5">
                {signalCategories.map(cat => {
                  const active = (form.signal_categories_shared || []).includes(cat);
                  return (
                    <button
                      key={cat}
                      onClick={() => {
                        setForm(f => ({
                          ...f,
                          signal_categories_shared: active
                            ? (f.signal_categories_shared || []).filter(c => c !== cat)
                            : [...(f.signal_categories_shared || []), cat],
                        }));
                      }}
                      className={`text-[9px] font-mono px-2 py-1 border transition-colors ${active ? 'border-kova-purple/30 text-kova-purple bg-kova-purple/10' : 'border-kova-cyan/12 text-kova-slate'}`}
                    >
                      {cat.replace(/_/g, ' ')}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Commons Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] font-medium">Commons Contribution</div>
              <div className="text-[9px] font-mono text-kova-slate">Contribute abstracted signals to the MERA Commons pool</div>
            </div>
            <button
              onClick={() => setForm(f => ({ ...f, commons_contribution_enabled: !f.commons_contribution_enabled }))}
              className={`w-10 h-5 rounded-full transition-colors ${form.commons_contribution_enabled ? 'bg-kova-green' : 'bg-kova-cyan/12'}`}
            >
              <div className={`w-4 h-4 bg-white rounded-full transition-transform ${form.commons_contribution_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* Data Residency */}
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-medium">Data Residency</div>
            <select
              value={form.data_residency || 'us'}
              onChange={e => setForm(f => ({ ...f, data_residency: e.target.value }))}
              className="bg-kova-card border border-kova-cyan/12 text-[10px] font-mono text-kova-silv px-2 py-1"
            >
              <option value="us">US</option>
              <option value="eu">EU</option>
              <option value="ap">Asia-Pacific</option>
            </select>
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={save} size="sm" className="rounded-none bg-kova-cyan/15 text-kova-cyan border border-kova-cyan/30 hover:bg-kova-cyan/25 font-mono text-[9px] uppercase tracking-wider" data-testid="save-governance-btn">Save</Button>
            <Button onClick={() => { setEditing(false); setForm(governance || {}); }} size="sm" variant="outline" className="rounded-none font-mono text-[9px]">Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Signal Exchange', value: governance?.signal_exchange_enabled ? 'Enabled' : 'Disabled', color: governance?.signal_exchange_enabled ? '#0ABFA3' : '#3D5A70' },
            { label: 'Commons', value: governance?.commons_contribution_enabled ? 'Contributing' : 'Read-only', color: governance?.commons_contribution_enabled ? '#1D9E75' : '#3D5A70' },
            { label: 'Data Residency', value: (governance?.data_residency || 'us').toUpperCase(), color: '#7A9AB0' },
            { label: 'Audit Log', value: governance?.audit_log_enabled ? 'Enabled' : 'Disabled', color: governance?.audit_log_enabled ? '#1D9E75' : '#3D5A70' },
          ].map(item => (
            <div key={item.label} className="px-3 py-2 border border-kova-cyan/12 bg-kova-bg">
              <div className="text-[9px] font-mono text-kova-slate uppercase tracking-wider">{item.label}</div>
              <div className="text-[12px] font-mono mt-0.5" style={{ color: item.color }}>{item.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubConstellationCard({ sub, connections, onDelete, onEdit }) {
  const systemNames = sub.system_ids.map(id => {
    const conn = connections.find(c => c.connection_id === id);
    return conn ? conn.name : id;
  });

  return (
    <div className="border border-kova-cyan/12 bg-kova-bg p-3 space-y-2" data-testid={`sub-constellation-${sub.sub_constellation_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TreeStructure size={12} className="text-kova-purple" />
          <span className="text-[13px] font-heading font-bold">{sub.name}</span>
          <span className="text-[9px] font-mono text-kova-slate">{sub.system_ids.length} systems</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onEdit(sub)} className="text-kova-slate hover:text-kova-cyan p-1" data-testid={`edit-sub-${sub.sub_constellation_id}`}><Pencil size={12} /></button>
          <button onClick={() => onDelete(sub.sub_constellation_id)} className="text-kova-slate hover:text-kova-red p-1" data-testid={`delete-sub-${sub.sub_constellation_id}`}><Trash size={12} /></button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {systemNames.map((name, i) => (
          <span key={i} className="text-[9px] font-mono px-2 py-0.5 bg-kova-card border border-kova-cyan/12 text-kova-silv">{name}</span>
        ))}
      </div>
    </div>
  );
}

export default function ConstellationPage() {
  const [constellation, setConstellation] = useState(null);
  const [subs, setSubs] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateSub, setShowCreateSub] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [subForm, setSubForm] = useState({ name: '', system_ids: [] });

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/constellation`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/constellation/sub`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/connections`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
    ]).then(([c, s, conns]) => {
      if (c) setConstellation(c);
      setSubs(s);
      setConnections(conns);
    }).catch(() => toast.error('Failed to load constellation data'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  function updateGovernance(gov) {
    fetch(`${API}/constellation/governance`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(gov),
    }).then(r => r.ok ? r.json() : Promise.reject())
      .then(() => { toast.success('Governance updated'); fetchAll(); })
      .catch(() => toast.error('Failed to update governance'));
  }

  function createSub() {
    if (!subForm.name.trim() || subForm.system_ids.length === 0) { toast.error('Name and at least one system required'); return; }
    const url = editingSub ? `${API}/constellation/sub/${editingSub.sub_constellation_id}` : `${API}/constellation/sub`;
    const method = editingSub ? 'PATCH' : 'POST';
    fetch(url, {
      method, credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(subForm),
    }).then(r => {
      if (r.status === 403) return r.json().then(d => { toast.error(d.detail); throw new Error(); });
      if (!r.ok) throw new Error();
      return r.json();
    }).then(() => {
      toast.success(editingSub ? 'Sub-Constellation updated' : 'Sub-Constellation created');
      setShowCreateSub(false); setEditingSub(null); setSubForm({ name: '', system_ids: [] }); fetchAll();
    }).catch(() => {});
  }

  function deleteSub(subId) {
    fetch(`${API}/constellation/sub/${subId}`, { method: 'DELETE', credentials: 'include' })
      .then(r => { if (r.ok) { toast.success('Deleted'); fetchAll(); } })
      .catch(() => toast.error('Delete failed'));
  }

  function openEdit(sub) {
    setEditingSub(sub);
    setSubForm({ name: sub.name, system_ids: [...sub.system_ids] });
    setShowCreateSub(true);
  }

  function toggleSystem(connId) {
    setSubForm(f => ({
      ...f,
      system_ids: f.system_ids.includes(connId)
        ? f.system_ids.filter(id => id !== connId)
        : [...f.system_ids, connId],
    }));
  }

  if (loading) return <div className="flex items-center justify-center h-64"><span className="font-mono text-kova-cyan animate-pulse">Loading constellation...</span></div>;

  const subLimit = constellation?.sub_constellation_limit;
  const canCreateSub = subLimit === -1 || subs.length < subLimit;

  return (
    <div className="space-y-6" data-testid="constellation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-lg">Constellation</h1>
          <p className="text-[10px] font-mono text-kova-slate mt-0.5">Your bounded intelligence unit · {constellation?.system_count || 0} connected systems</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-mono px-2 py-0.5 border border-kova-cyan/12 text-kova-slate" style={{ color: constellation?.tier === 'founding' ? '#FFD700' : undefined }}>{constellation?.tier_label}</span>
        </div>
      </div>

      {/* Constellation Identity */}
      <div className="card-brutal p-4" data-testid="constellation-identity">
        <div className="flex items-center gap-3">
          <Globe size={18} className="text-kova-cyan" />
          <div>
            <div className="text-[14px] font-heading font-bold">{constellation?.name}</div>
            <div className="text-[10px] font-mono text-kova-slate">{constellation?.constellation_id}</div>
          </div>
        </div>
      </div>

      {/* Governance */}
      <div className="card-brutal p-4">
        <GovernancePanel
          governance={constellation?.governance}
          available={{
            signal_exchange_available: constellation?.signal_exchange_available,
            commons_full_access: constellation?.commons_full_access,
          }}
          onUpdate={updateGovernance}
        />
      </div>

      {/* Sub-Constellations */}
      <div className="card-brutal p-4 space-y-3" data-testid="sub-constellations-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TreeStructure size={14} className="text-kova-purple" />
            <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">Sub-Constellations</span>
            <span className="text-[9px] font-mono text-kova-slate">
              {subs.length}{subLimit !== -1 ? ` / ${subLimit}` : ''}
            </span>
          </div>
          {subLimit === 0 ? (
            <span className="text-[9px] font-mono text-kova-slate flex items-center gap-1"><Lock size={10} />Growth plan required</span>
          ) : (
            <Button
              onClick={() => { setEditingSub(null); setSubForm({ name: '', system_ids: [] }); setShowCreateSub(true); }}
              disabled={!canCreateSub}
              size="sm" variant="outline"
              className="rounded-none font-mono text-[9px] uppercase tracking-wider border-kova-purple/30 text-kova-purple"
              data-testid="create-sub-btn"
            >
              <Plus size={10} className="mr-1" />Create
            </Button>
          )}
        </div>

        {subs.length === 0 ? (
          <div className="py-6 text-center">
            <TreeStructure size={32} className="text-kova-cyan/12 mx-auto mb-2" />
            <p className="text-[11px] font-mono text-kova-slate">No Sub-Constellations yet</p>
            <p className="text-[9px] font-mono text-kova-cyan/25 mt-1">Group systems by department, use case, or governance boundary</p>
          </div>
        ) : (
          <div className="space-y-2">
            {subs.map(sub => (
              <SubConstellationCard key={sub.sub_constellation_id} sub={sub} connections={connections} onDelete={deleteSub} onEdit={openEdit} />
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Sub Modal */}
      <AnimatePresence>
        {showCreateSub && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowCreateSub(false)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()} className="bg-kova-card border border-kova-cyan/12 w-[460px] max-h-[520px] overflow-hidden" data-testid="create-sub-modal">
              <div className="flex items-center justify-between px-4 py-3 border-b border-kova-cyan/12">
                <span className="text-sm font-heading font-bold">{editingSub ? 'Edit' : 'Create'} Sub-Constellation</span>
                <button onClick={() => setShowCreateSub(false)} className="text-kova-slate hover:text-white"><X size={16} /></button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="text-[10px] font-mono text-kova-slate uppercase tracking-wider block mb-1">Name</label>
                  <input
                    value={subForm.name}
                    onChange={e => setSubForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Finance Stack, DevOps Pipeline"
                    className="w-full bg-kova-bg border border-kova-cyan/12 text-sm font-mono text-white px-3 py-2 focus:outline-none focus:border-kova-cyan/50"
                    data-testid="sub-name-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono text-kova-slate uppercase tracking-wider block mb-2">Select Systems ({subForm.system_ids.length} selected)</label>
                  <div className="max-h-[220px] overflow-y-auto space-y-1 border border-kova-cyan/12 bg-kova-bg p-2" data-testid="system-selector">
                    {connections.length === 0 ? (
                      <p className="text-[10px] font-mono text-kova-slate p-2 text-center">No connections available</p>
                    ) : connections.map(conn => {
                      const selected = subForm.system_ids.includes(conn.connection_id);
                      return (
                        <button
                          key={conn.connection_id}
                          onClick={() => toggleSystem(conn.connection_id)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 text-left transition-colors ${selected ? 'bg-kova-purple/10 border border-kova-purple/30' : 'border border-transparent hover:bg-white/5'}`}
                          data-testid={`system-${conn.connection_id}`}
                        >
                          {selected ? <CheckCircle size={12} weight="fill" className="text-kova-purple" /> : <div className="w-3 h-3 border border-kova-cyan/12" />}
                          <span className="text-[11px] font-mono text-kova-silv">{conn.name}</span>
                          <span className="text-[9px] font-mono text-kova-cyan/25 ml-auto truncate max-w-[140px]">{conn.base_url}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={createSub}
                    size="sm"
                    className="rounded-none bg-kova-purple/15 text-kova-purple border border-kova-purple/30 hover:bg-kova-purple/25 font-mono text-[9px] uppercase tracking-wider"
                    data-testid="save-sub-btn"
                  >
                    {editingSub ? 'Update' : 'Create'}
                  </Button>
                  <Button onClick={() => setShowCreateSub(false)} size="sm" variant="outline" className="rounded-none font-mono text-[9px]">Cancel</Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
